// ─────────────────────────────────────────────────────────────
// 智能体状态枚举
// ─────────────────────────────────────────────────────────────
export enum AgentStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

export enum AgentTaskStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum AgentTaskNodeStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

// ─────────────────────────────────────────────────────────────
// 智能体响应
// ─────────────────────────────────────────────────────────────
export interface AgentResponse {
  id: string;
  name: string;
  description: string | null;
  flowData: Record<string, any>;
  isTemplate: boolean;
  taskType: string | null;
  version: number;
  status: AgentStatus;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// ─────────────────────────────────────────────────────────────
// 创建和更新请求
// ─────────────────────────────────────────────────────────────
export interface CreateAgentRequest {
  name: string;
  description?: string;
  flowData: Record<string, any>;
  isTemplate?: boolean;
  taskType?: string;
  status?: AgentStatus;
}

export interface UpdateAgentRequest {
  name?: string;
  description?: string;
  flowData?: Record<string, any>;
  isTemplate?: boolean;
  taskType?: string;
  version?: number;
  status?: AgentStatus;
}

// ─────────────────────────────────────────────────────────────
// 查询参数
// ─────────────────────────────────────────────────────────────
export interface AgentQueryParams {
  current?: number;
  size?: number;
  taskType?: string;
  status?: AgentStatus;
  isTemplate?: boolean;
}

// ─────────────────────────────────────────────────────────────
// 验证
// ─────────────────────────────────────────────────────────────
export interface ValidateAgentRequest {
  flowData: FlowGramJSON;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// ─────────────────────────────────────────────────────────────
// 任务响应
// ─────────────────────────────────────────────────────────────
export interface AgentTaskResponse {
  id: string;
  agentId: string;
  taskType: string;
  businessId: string;
  source: string | null;
  status: AgentTaskStatus;
  inputData: Record<string, any>;
  outputData: Record<string, any> | null;
  currentNodeId: string | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentTaskNodeResponse {
  id: string;
  taskId: string;
  nodeId: string;
  nodeType: string;
  nodeName: string | null;
  status: AgentTaskNodeStatus;
  inputData: Record<string, any> | null;
  outputData: Record<string, any> | null;
  error: string | null;
  durationMs: number | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────────────────────────────
// FlowGram JSON 结构
// ─────────────────────────────────────────────────────────────
export interface FlowGramNode {
  id: string;
  type: string;
  meta?: { position?: { x: number; y: number } };
  data: Record<string, any>;
}

export interface FlowGramEdge {
  sourceNodeID: string;
  targetNodeID: string;
  sourcePortID?: string;
  targetPortID?: string;
}

export interface FlowGramJSON {
  nodes: FlowGramNode[];
  edges: FlowGramEdge[];
}

// ─────────────────────────────────────────────────────────────
// DAG 节点类型
// ─────────────────────────────────────────────────────────────
export type WorkflowNodeType =
  | 'start' | 'end' | 'condition' | 'loop' | 'log'
  | 'llm' | 'ocr' | 'asr'
  | 'http' | 'code'
  | 'document-parse';

// ─────────────────────────────────────────────────────────────
// DAG 引擎内部类型
// ─────────────────────────────────────────────────────────────
export interface ParsedNode {
  id: string;
  type: WorkflowNodeType;
  data: Record<string, any>;
  meta: { position?: { x: number; y: number } };
}

export interface DAGEdge {
  targetNodeID: string;
  sourcePortID?: string;
  targetPortID?: string;
}

export interface DAG {
  nodes: Map<string, ParsedNode>;
  adjacencyList: Map<string, DAGEdge[]>;
  reverseAdjacencyList: Map<string, string[]>;
  startNodeId: string;
  endNodeIds: string[];
}

export interface NodeExecutionContext {
  node: ParsedNode;
  inputData: Record<string, any>;
  taskId: string;
  workflowInput: Record<string, any>;
  emitProgress?: (payload: NodeProgressPayload) => void;
}

export interface NodeProgressPayload {
  processed: number;
  total: number;
  partial?: any;
  message?: string;
}

export interface NodeOutput {
  success: boolean;
  data: Record<string, any>;
  error?: string;
  metrics?: { duration: number; tokenUsage?: { input: number; output: number } };
}

// ─────────────────────────────────────────────────────────────
// SSE 事件
// ─────────────────────────────────────────────────────────────
export interface AgentTaskEvent {
  type: 'task_created' | 'node_start' | 'node_progress' | 'node_complete' | 'node_error' | 'task_complete' | 'task_error';
  taskId: string;
  nodeId?: string;
  nodeName?: string;
  nodeType?: string;
  data?: Record<string, any>;
  error?: string;
  durationMs?: number;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────
// 任务类型 Schema
// ─────────────────────────────────────────────────────────────
export type SchemaFieldType = 'string' | 'number' | 'boolean' | 'array' | 'object';

export interface SchemaField {
  key: string;
  type: SchemaFieldType;
  required?: boolean;
  description: string;
  example?: string;
}

export interface TaskTypeSchema {
  label: string;
  description: string;
  input: SchemaField[];
  output: SchemaField[];
}

export const TASK_TYPE_SCHEMAS: Record<string, TaskTypeSchema> = {
  'image-recognition': {
    label: '图片识别',
    description: 'OCR、图像分类、物体检测',
    input: [{ key: 'imageUrl', type: 'string', required: true, description: '图片 URL' }],
    output: [{ key: 'text', type: 'string', description: '识别出的文字内容' }],
  },
  'document-recognition': {
    label: '文档识别',
    description: 'PDF/Word 解析、表格提取',
    input: [{ key: 'documentUrl', type: 'string', required: true, description: '文档 URL' }],
    output: [{ key: 'text', type: 'string', description: '提取的文本内容' }],
  },
  'audio-recognition': {
    label: '音频识别',
    description: '语音转文字、情感识别',
    input: [{ key: 'audioUrl', type: 'string', required: true, description: '音频文件 URL' }],
    output: [{ key: 'transcript', type: 'string', description: '语音转文字结果' }],
  },
  'custom': {
    label: '自定义任务',
    description: '自定义输入输出字段',
    input: [{ key: 'input', type: 'object', description: '自定义输入数据' }],
    output: [{ key: 'output', type: 'object', description: '自定义输出数据' }],
  },
};

export const TASK_TYPE_OPTIONS = Object.entries(TASK_TYPE_SCHEMAS).map(([value, schema]) => ({
  value,
  label: schema.label,
  description: schema.description,
}));
